// A heading that is also a person switcher: the name shown opens a menu of people, each with
// a count beside them, in sections split by dividers. My actions and the Amazon overview's
// Needed from Amazon both use it (docs/Y2026-Q4/design-amazon-item-owners.md section 2.5).
import { type ReactNode, useEffect, useRef, useState } from "react";

import { Chevron } from "./Chevron";
import styles from "./PersonSwitcher.module.css";

export interface SwitcherOption {
  key: string;
  label: ReactNode;
  count: string;
}

export const PersonSwitcher = ({
  title,
  sections,
  current,
  onChoose,
  wrap,
}: {
  title: ReactNode;
  /** Shown in order, a divider between each. */
  sections: SwitcherOption[][];
  current: string;
  onChoose: (key: string) => void;
  /** What holds the title button: the page's h1, or a plain span. */
  wrap: (button: ReactNode) => ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent && event.key !== "Escape") return;
      if (event instanceof MouseEvent && ref.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);
  const shown = sections.filter((s) => s.length > 0);
  return (
    <div className={styles.picker} ref={ref}>
      {wrap(
        <button
          type="button"
          className={styles.title}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
        >
          {title}
          <span className={styles.caret}>
            <Chevron open={open} />
          </span>
        </button>,
      )}
      {open && (
        <div className={styles.menu} role="menu">
          {shown.map((section, i) => (
            <div key={i} className={i > 0 ? styles.section : undefined}>
              {section.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  role="menuitemradio"
                  aria-checked={o.key === current}
                  className={`${styles.option} ${o.key === current ? styles.current : ""}`}
                  onClick={() => {
                    setOpen(false);
                    onChoose(o.key);
                  }}
                >
                  <span>{o.label}</span>
                  <span className={styles.count}>{o.count}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
